import { Place } from "../types/place";
import { EntityController } from "./entityController";
import { useNavigate } from "react-router-dom";
import usePlaceQuery from "../hooks/query/usePlaceQuery";
import usePlaceLogic from "../hooks/logic/placeLogic";

export interface PlaceController extends EntityController<Place> {
  displayName: (data: Place) => string;
  viewTitle: (data: Place) => string;
  editTitle: (data: Place) => string;
}

const usePlaceController = (id: string | null): PlaceController => {
  const { useFetchById, useCreate, useDeleteById, useUpdate } = usePlaceQuery();
  const { defaultPlace, userEnums, metadataFieldNames, getDisplayName } =
    usePlaceLogic();
  const fetchById = useFetchById(id || "");
  const update = useUpdate();
  const create = useCreate();
  const deleteById = useDeleteById();
  const navigate = useNavigate();

  const fetchData = async (): Promise<Place> => {
    if (!id) {
      return defaultPlace;
    }
    const res = await fetchById.refetch();
    const placeData = res.data;
    if (!placeData || res.isError) {
      throw new Error("Place data not found");
    }
    return placeData;
  };

  const isCreateMode = id === null;

  const displayName = (data: Place) => getDisplayName(data);

  const viewTitle = (data: Place) => displayName(data);

  const editTitle = (data: Place) =>
    `${isCreateMode ? "Create New" : "Edit"} ${displayName(data)}`;

  const onSubmit = async (data: Partial<Place>) => {
    const updateData: Partial<Place> = {
      name: data.name,
      entity_status_of_proofreading: data.entity_status_of_proofreading,
      other_designations: data.other_designations,
      external_links: data.external_links,
      region: data.region,
      type: data.type,
      notes: data.notes,
      attributes_metadata: {
        ...defaultPlace.attributes_metadata,
        ...data.attributes_metadata,
      },
      internal_notes: data.internal_notes,
    };

    if (isCreateMode) {
      updateData.cultural_sphere = data.cultural_sphere;
    }

    let result: Place | null;
    if (id) {
      result = await update.mutateAsync({
        id,
        data: { _rev: data._rev, ...updateData },
      });
      if (!result || update.isError) {
        throw new Error("Failed to update Place");
      }
    } else {
      result = await create.mutateAsync(updateData);
      if (!result || create.isError) {
        throw new Error("Failed to create Place");
      }
    }

    return result;
  };

  const onDelete = async () => {
    if (!id) {
      throw new Error("Place ID is required for deletion");
    }

    await deleteById.mutateAsync(id);
    if (deleteById.isError) {
      throw new Error("Failed to delete place");
    }
  };

  const navigateBack = () => navigate(-1);

  const deleteWarningMessage = "Are you sure you want to delete this place?";

  const getEditRoute = () => `/places/${id}/edit`;

  return {
    fetchData,
    userEnums,

    displayName,
    viewTitle,
    editTitle,
    title: viewTitle,

    defaultValues: defaultPlace,
    metadataFieldNames,
    isCreateMode,
    onSubmit,
    onDelete,
    deleteWarningMessage,
    formCallback: navigateBack,

    getEditRoute,
  };
};

export default usePlaceController;
